import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Search, X } from "lucide-react";
import { searchContent, scrollToSection, SearchItem } from "../utils/searchData";

export function Header() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (query.trim()) {
      const results = searchContent(query);
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  // Handle search result click
  const handleResultClick = (result: SearchItem) => {
    scrollToSection(result.sectionId);
    setSearchQuery("");
    setShowResults(false);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setShowResults(false);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl text-primary">YOGIVITY</h1>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            <a
              href="#home"
              className="text-foreground hover:text-primary transition-colors"
            >
              Kezdőlap
            </a>
            <a
              href="#services"
              className="text-foreground hover:text-primary transition-colors"
            >
              Szolgáltatásaim
            </a>
            <a
              href="#about"
              className="text-foreground hover:text-primary transition-colors"
            >
              Rólam
            </a>
            <a
              href="#contact"
              className="text-foreground hover:text-primary transition-colors"
            >
              Kapcsolat
            </a>
            <a
              href="/online-program"
              className="text-foreground hover:text-primary transition-colors"
            >
              Online Program
            </a>
          </nav>

          <div className="flex items-center space-x-4">
            {/* Search Input */}
            <div className="relative xxxs:hidden xxs:hidden" ref={searchRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Keresés"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onFocus={() => searchQuery && setShowResults(true)}
                  className="pl-10 pr-10 w-48 bg-input-background border-border"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg max-h-80 overflow-y-auto z-50">
                  {searchResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => handleResultClick(result)}
                      className="w-full text-left px-4 py-3 hover:bg-secondary/50 border-b border-border/50 last:border-b-0 transition-colors"
                    >
                      <div className="flex items-start space-x-3">
                        <Search className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">
                            {result.title}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {result.section}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {result.content.length > 100 
                              ? `${result.content.substring(0, 100)}...` 
                              : result.content}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* No Results Message */}
              {showResults && searchQuery && searchResults.length === 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-lg shadow-lg p-4 z-50">
                  <div className="text-sm text-muted-foreground text-center">
                    Nincs találat a következőre: "{searchQuery}"
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="
                bg-primary 
                text-primary-foreground
                hover:bg-[#6d5a44]          /* Darker shade on hover */
                active:bg-[#5a4a38]         /* Even darker when clicked */
                transition-colors 
                duration-200
                transform 
                hover:scale-[1.02]          /* Subtle grow effect */
                active:scale-[0.98]         /* Subtle press effect */
                shadow-md 
                hover:shadow-lg             /* Enhanced shadow on hover */
                hover:shadow-primary/30     /* Colored glow */
                border 
                border-primary/20           /* Subtle border */
                hover:border-primary/40     /* More visible border on hover */
              "
            >
              Elkezdem a YOGIVITY-t
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
